import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Box,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { getBorrowerCommPrefs, updateBorrowerCommPrefs } from "../../service/otpApi";

const PHONE_LABELS = ["Mobile", "Home", "Work"];
const EMAIL_LABELS = ["Primary", "Work", "Other"];

const CommunicationPreferences = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferredMethod, setPreferredMethod] = useState("both");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      fetchPrefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchPrefs = async () => {
    try {
      setLoading(true);
      setErrors({});
      const res = await getBorrowerCommPrefs();
      if (res?.status === 200) {
        const data = res.data;
        setPreferredMethod(data.preferredMethod || "both");
        setPhoneNumbers(
          data.phoneNumbers?.length > 0
            ? data.phoneNumbers.map((p) => ({ ...p }))
            : [{ number: "", label: "Mobile", isPrimary: true }]
        );
        setEmailAddresses(
          data.emailAddresses?.length > 0
            ? data.emailAddresses.map((e) => ({ ...e }))
            : [{ address: "", label: "Primary", isPrimary: true }]
        );
      }
    } catch (err) {
      console.error("Error fetching communication preferences:", err);
      setSnackbar({ open: true, message: "Failed to load preferences.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (preferredMethod === "text" || preferredMethod === "both") {
      const validPhones = phoneNumbers.filter((p) => p.number.trim());
      if (validPhones.length === 0) {
        newErrors.phones = "At least one phone number is required for text notifications.";
      }
      phoneNumbers.forEach((p, i) => {
        if (p.number.trim()) {
          const digits = p.number.replace(/\D/g, "");
          if (digits.length < 10) {
            newErrors[`phone_${i}`] = "Enter a valid 10-digit phone number.";
          }
        }
      });
    }

    if (preferredMethod === "email" || preferredMethod === "both") {
      const validEmails = emailAddresses.filter((e) => e.address.trim());
      if (validEmails.length === 0) {
        newErrors.emails = "At least one email address is required for email notifications.";
      }
      emailAddresses.forEach((e, i) => {
        if (e.address.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.address.trim())) {
          newErrors[`email_${i}`] = "Enter a valid email address.";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      // Filter out empty entries
      const filteredPhones = phoneNumbers.filter((p) => p.number.trim());
      const filteredEmails = emailAddresses.filter((e) => e.address.trim());

      const res = await updateBorrowerCommPrefs({
        preferredMethod,
        phoneNumbers: filteredPhones,
        emailAddresses: filteredEmails,
      });

      if (res?.status === 200) {
        setSnackbar({ open: true, message: "Preferences saved successfully!", severity: "success" });
        onClose();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save preferences.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Phone handlers
  const addPhone = () => {
    setPhoneNumbers([...phoneNumbers, { number: "", label: "Mobile", isPrimary: false }]);
  };

  const removePhone = (index) => {
    const updated = phoneNumbers.filter((_, i) => i !== index);
    if (updated.length > 0 && !updated.some((p) => p.isPrimary)) {
      updated[0].isPrimary = true;
    }
    setPhoneNumbers(updated);
  };

  const updatePhone = (index, field, value) => {
    const updated = [...phoneNumbers];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "isPrimary" && value) {
      updated.forEach((p, i) => {
        if (i !== index) p.isPrimary = false;
      });
    }
    setPhoneNumbers(updated);
  };

  // Email handlers
  const addEmail = () => {
    setEmailAddresses([...emailAddresses, { address: "", label: "Other", isPrimary: false }]);
  };

  const removeEmail = (index) => {
    const updated = emailAddresses.filter((_, i) => i !== index);
    if (updated.length > 0 && !updated.some((e) => e.isPrimary)) {
      updated[0].isPrimary = true;
    }
    setEmailAddresses(updated);
  };

  const updateEmail = (index, field, value) => {
    const updated = [...emailAddresses];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "isPrimary" && value) {
      updated.forEach((e, i) => {
        if (i !== index) e.isPrimary = false;
      });
    }
    setEmailAddresses(updated);
  };

  const showPhones = preferredMethod === "text" || preferredMethod === "both";
  const showEmails = preferredMethod === "email" || preferredMethod === "both";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Communication Preferences</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Choose how you'd like your loan officer to contact you.
              </Typography>

              <FormControl>
                <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Preferred Contact Method</FormLabel>
                <RadioGroup
                  value={preferredMethod}
                  onChange={(e) => {
                    setPreferredMethod(e.target.value);
                    setErrors({});
                  }}
                >
                  <FormControlLabel value="text" control={<Radio />} label="Text Message" />
                  <FormControlLabel value="email" control={<Radio />} label="Email" />
                  <FormControlLabel value="both" control={<Radio />} label="Both" />
                </RadioGroup>
              </FormControl>

              {showPhones && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Phone Numbers
                    </Typography>
                    {errors.phones && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {errors.phones}
                      </Typography>
                    )}
                    {phoneNumbers.map((phone, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1.5 }}
                      >
                        <TextField
                          size="small"
                          label="Phone Number"
                          value={phone.number}
                          onChange={(e) => updatePhone(index, "number", e.target.value)}
                          error={!!errors[`phone_${index}`]}
                          helperText={errors[`phone_${index}`]}
                          placeholder="(555) 123-4567"
                          sx={{ flex: 1 }}
                        />
                        <Select
                          size="small"
                          value={phone.label}
                          onChange={(e) => updatePhone(index, "label", e.target.value)}
                          sx={{ minWidth: 100 }}
                        >
                          {PHONE_LABELS.map((l) => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                          ))}
                        </Select>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={phone.isPrimary}
                              onChange={() => updatePhone(index, "isPrimary", true)}
                              size="small"
                            />
                          }
                          label="Primary"
                          sx={{ mr: 0 }}
                        />
                        {phoneNumbers.length > 1 && (
                          <IconButton size="small" onClick={() => removePhone(index)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addPhone}
                      sx={{ mt: 0.5 }}
                    >
                      Add Phone Number
                    </Button>
                  </Box>
                </>
              )}

              {showEmails && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Email Addresses
                    </Typography>
                    {errors.emails && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {errors.emails}
                      </Typography>
                    )}
                    {emailAddresses.map((email, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1.5 }}
                      >
                        <TextField
                          size="small"
                          label="Email Address"
                          value={email.address}
                          onChange={(e) => updateEmail(index, "address", e.target.value)}
                          error={!!errors[`email_${index}`]}
                          helperText={errors[`email_${index}`]}
                          placeholder="you@example.com"
                          sx={{ flex: 1 }}
                        />
                        <Select
                          size="small"
                          value={email.label}
                          onChange={(e) => updateEmail(index, "label", e.target.value)}
                          sx={{ minWidth: 100 }}
                        >
                          {EMAIL_LABELS.map((l) => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                          ))}
                        </Select>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={email.isPrimary}
                              onChange={() => updateEmail(index, "isPrimary", true)}
                              size="small"
                            />
                          }
                          label="Primary"
                          sx={{ mr: 0 }}
                        />
                        {emailAddresses.length > 1 && (
                          <IconButton size="small" onClick={() => removeEmail(index)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addEmail}
                      sx={{ mt: 0.5 }}
                    >
                      Add Email Address
                    </Button>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CommunicationPreferences;
